/* eslint-disable no-restricted-imports -- this module is the wrapper the ban points people to. */
import {
  DefaultError,
  QueryClient,
  QueryKey,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
/* eslint-enable no-restricted-imports */
import {useCallback, useLayoutEffect, useRef} from "react";

class SuspenseError extends Error {}

// React 18 fiber tag for a Suspense boundary, whose non-null `memoizedState`
// (a SuspenseState) marks it as rendering its fallback. The suspending subtree is
// hidden (Offscreen), not deleted, so its fibers stay walkable. A DOM node caches
// a fixed fiber; React alternates which of it and its `alternate` is the live tree
// across commits, so the fallback state can sit on either — both are read.
const SUSPENSE_COMPONENT_TAG = 13;

interface Fiber {
  tag: number;
  return: Fiber | null;
  memoizedState: unknown;
  alternate: Fiber | null;
}

function fiberFromNode(node: Element): Fiber | null {
  const key = Object.keys(node).find(k => k.startsWith("__reactFiber$"));
  return key ? ((node as unknown as Record<string, Fiber>)[key] ?? null) : null;
}

// True when an ancestor Suspense boundary of `node` is showing its fallback —
// i.e. the node is unmounting because that boundary suspended and hid its content
// subtree, not because it was plainly removed (navigation, close). Reads
// undocumented fiber internals, so it fails closed: any shape change (e.g. a
// React upgrade) yields `false` rather than throwing inside an unmount cleanup.
function unmountedByAncestorSuspense(node: Element): boolean {
  try {
    for (let fiber = fiberFromNode(node); fiber; fiber = fiber.return) {
      if (
        fiber.tag === SUSPENSE_COMPONENT_TAG &&
        (fiber.memoizedState !== null || fiber.alternate?.memoizedState != null)
      ) {
        return true;
      }
    }
  } catch {
    // Intentionally empty: see fail-closed note above.
  }
  return false;
}

export function useSuspenseQueryWithDiagnostics<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> {
  try {
    return useSuspenseQuery(options, queryClient);
  } catch (ex) {
    DIAGNOSTICS.reportPossibleSuspense(ex);
    throw ex;
  }
}

// Mirror `useSuspenseQueries`' full overloaded signature (both the tuple and the
// mapped-array `queries` forms) so callers keep their `combine` return-type
// inference; a hand-written signature can only express the stricter overload.
export const useSuspenseQueriesWithDiagnostics: typeof useSuspenseQueries = (...args) => {
  try {
    return useSuspenseQueries(...args);
  } catch (ex) {
    DIAGNOSTICS.reportPossibleSuspense(ex);
    throw ex;
  }
};

// Attach the returned ref to a DOM node inside the dialog. When the dialog
// unmounts while open, the node's fiber is walked to see whether an ancestor
// Suspense boundary tore it down; only then is it reported.
export function useDialogDiagnostics(open: boolean): (node: Element | null) => void {
  const wasOpen = useRef(open);
  wasOpen.current = open;
  const nodeRef = useRef<Element | null>(null);
  useLayoutEffect(
    () => () => {
      if (wasOpen.current && nodeRef.current && unmountedByAncestorSuspense(nodeRef.current)) {
        DIAGNOSTICS.reportSuspenseError(new Error("Dialog unmounted whilst open"));
      }
    },
    [],
  );
  // Keep the last non-null node. `DialogContent` relays this ref from a
  // dependency-less passive effect that pushes `null` through on every closed
  // render (Chakra `unmountOnExit`); the teardown cleanup still needs the node
  // those nulls would clobber.
  return useCallback((node: Element | null) => {
    if (node) nodeRef.current = node;
  }, []);
}

class Diagnostics {
  #activeSuspenseErrors: SuspenseError[] = [];
  debugSuspense = false;

  // This should be called from every hook which might suspend
  reportPossibleSuspense(ex: unknown) {
    if (ex instanceof Promise) {
      if (this.debugSuspense) {
        console.trace("Component suspended");
      }
      // The message is load-bearing for Sentry grouping: a message-less Error
      // collapses into the shared "No error message" catch-all issue, so a blank
      // marker would drag every symptom it's linked to into that bucket. A message
      // keeps the symptom in its own group.
      const error = new SuspenseError("Component suspended");
      this.#activeSuspenseErrors.push(error);
      // Remove the marker after 100ms. Guard the index: the timer can fire after
      // `reportSuspenseError` has reset the list, where `indexOf` returns -1 and an
      // unguarded `splice(-1, 1)` would evict an unrelated, still-active marker.
      setTimeout(() => {
        const i = this.#activeSuspenseErrors.indexOf(error);
        if (i !== -1) {
          this.#activeSuspenseErrors.splice(i, 1);
        }
      }, 100);
    }
  }

  // Reports a teardown that an ancestor suspension caused. The caller confirms
  // causation from the fiber tree (`unmountedByAncestorSuspense`), so ordinary
  // unmounting — navigation away from an open dialog — never reaches here.
  //
  // Any suspense markers recorded in the same commit are attached as the error's
  // `cause` — their stacks pin where the suspensions happened — so Sentry's
  // linkedErrors integration records a single event linking the symptom to its
  // candidate causes, rather than a swarm of decorrelated, message-less events.
  reportSuspenseError(err: Error = new Error("Invalid suspense")) {
    if (this.#activeSuspenseErrors.length > 0) {
      err.cause = new AggregateError(this.#activeSuspenseErrors, "Recent suspense events");
      this.#activeSuspenseErrors = [];
    }
    console.error(err);
  }
}

declare global {
  interface Window {
    Diagnostics: Diagnostics;
  }
}

export const DIAGNOSTICS = new Diagnostics();

// Dev-only console handle; guarded so the module is importable without a DOM (tests / SSR).
if (typeof window !== "undefined") {
  window.Diagnostics = DIAGNOSTICS;
}
